<template>
  <div class="order-confirmation">
    <div class="order-confirmation-container">
      <!-- Header with Icon -->
      <div class="confirmation-header">
        <div class="confirmation-icon-container">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="confirmation-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
        </div>
        <h2 class="confirmation-title">
          {{ $translate("Please check the summary of your order below.") }}
        </h2>
      </div>

      <div class="confirmation-message">
        <p v-html="recapitulationAgreeHtmlString"></p>
      </div>

      <!-- Order Summary -->
      <div class="summary-card">
        <div class="summary-header">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="summary-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 10H3"></path>
            <path d="M21 6H3"></path>
            <path d="M21 14H3"></path>
            <path d="M21 18H3"></path>
          </svg>
          <h3 class="summary-title">{{ $translate("Order Summary") }}</h3>
        </div>

        <div class="summary-content">
          <div
            v-for="(item, index) in cartStore.state.cart"
            :key="index"
            class="summary-item"
          >
            <div class="item-details">
              <p class="item-name">{{ item.title || item.name }}</p>
              <p v-if="item.subtitle" class="item-subtitle">
                {{ item.subtitle }}
              </p>
            </div>
            <div class="item-price">{{ item.price_formatted }}</div>
          </div>

          <div class="summary-total">
            <span class="total-label">{{ $translate("Total") }}</span>
            <span class="total-amount">{{
              cartStore.state.total.formatted
            }}</span>
          </div>
        </div>
      </div>

      <!-- Terms and Conditions Checkbox -->
      <div class="terms-section" :class="{ 'terms-error': showTermsError }">
        <div class="terms-checkbox-container">
          <div class="checkbox-wrapper">
            <input
              id="terms"
              type="checkbox"
              v-model="termsAccepted"
              class="terms-checkbox"
            />
            <div class="custom-checkbox">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="check-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
          </div>
          <label for="terms" class="terms-label">
            <span v-html="termsHtmlString"></span>
          </label>
        </div>
        <p v-if="showTermsError" class="terms-error-message">
          {{
            $translate("You must accept the terms and conditions to continue.")
          }}
        </p>
      </div>

      <!-- Create Order Button -->
      <div class="action-container">
        <button
          @click="confirmOrder"
          :disabled="!termsAccepted"
          class="confirm-button"
          :class="{ 'confirm-button-disabled': !termsAccepted }"
        >
          <div class="button-content">
            <svg
              v-if="termsAccepted"
              xmlns="http://www.w3.org/2000/svg"
              class="button-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              class="button-icon-disabled"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="16"></line>
              <line x1="8" y1="12" x2="16" y2="12"></line>
            </svg>
            <span class="button-text">{{
              $translate("Create Order with Payment Obligation")
            }}</span>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, inject, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { translate } from "@/utils/translate";
import { trackEvent } from "@/utils/trackingManager";

export default {
  name: "OrderConfirmation",
  emits: ["confirm"],

  setup(props, { emit }) {
    const cartStore = inject("cartStore");
    const router = useRouter();
    const termsAccepted = ref(true); // Consider setting initial state based on requirements (e.g., false)
    const showTermsError = ref(false);

    // Function to safely resolve URLs
    const resolveUrl = (name, params) => {
      try {
        return router.resolve({ name, params }).href;
      } catch (e) {
        console.error(
          `Error resolving URL for ${name} with params ${JSON.stringify(
            params
          )}:`,
          e
        );
        return "#"; // Fallback URL
      }
    };

    // Computed property to resolve URLs and prepare the translation
    // Computed property for the terms text (using article_73)
    const termsHtmlString = computed(() => {
      const termsUrl = resolveUrl("LegalPages", { id: "terms" });
      // Assuming 'article_73' only needs termsUrl based on the provided translation
      return translate("article_73", { termsUrl });
    });

    // NEW: Computed property for the recapitulation agreement text (using recapitulation_agree)
    const recapitulationAgreeHtmlString = computed(() => {
      const termsUrl = resolveUrl("LegalPages", { id: "terms" });
      // Assuming 'recapitulation_agree' also needs termsUrl
      return translate("recapitulation_agree", { termsUrl });
    });

    onMounted(() => {
      // Track the order confirmation step
      trackEvent("cart", "view_step", "order_confirmation", null, {
        step_number: 3,
        event_name: "checkout_progress",
      });
    });

    const confirmOrder = () => {
      if (!termsAccepted.value) {
        showTermsError.value = true;
        return;
      }

      showTermsError.value = false;

      // Client-side checkout progress event
      // Basic tracking without purchase information (backend will handle the actual conversion)
      trackEvent(
        "cart", 
        "order_confirmed", 
        "checkout_step", 
        null, 
        { step_number: 4 }
      );

      emit("confirm");
    };

    return {
      cartStore,
      termsAccepted,
      showTermsError,
      confirmOrder,
      termsHtmlString,
      recapitulationAgreeHtmlString,
    };
  },
};
</script>

<style scoped>
.order-confirmation {
  padding: 2rem 1rem;
  background-color: #f9fafb;
  min-height: 100%;
}

.order-confirmation-container {
  max-width: 800px;
  margin: 0 auto;
  background-color: white;
  border-radius: 1rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

/* Header Styles */
.confirmation-header {
  background-color: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  color: #1f2937;
  padding: 2rem;
  text-align: center;
  position: relative;
}

.confirmation-icon-container {
  background-color: white;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.confirmation-icon {
  width: 32px;
  height: 32px;
  color: #10b981;
}

.confirmation-title {
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.3;
}

/* Confirmation Message */
.confirmation-message {
  background-color: #f9fafb;
  border-left: 4px solid #64748b;
  padding: 1.25rem;
  margin: 1.5rem;
  border-radius: 0.5rem;
  font-size: 0.95rem;
  color: #334155;
  line-height: 1.5;
}

.confirmation-message a {
  color: #10b981;
  text-decoration: underline;
  font-weight: 500;
}

.confirmation-message a:hover {
  color: #059669;
}

/* Summary Card */
.summary-card {
  margin: 0 1.5rem 1.5rem;
  background-color: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.summary-header {
  display: flex;
  align-items: center;
  padding: 1.25rem;
  background-color: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.summary-icon {
  width: 1.25rem;
  height: 1.25rem;
  margin-right: 0.75rem;
  color: #4b5563;
}

.summary-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.summary-content {
  padding: 1rem 1.25rem;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  padding: 0.875rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.summary-item:last-of-type {
  border-bottom: none;
}

.item-details {
  flex: 1;
  min-width: 0;
  padding-right: 1rem;
}

.item-name {
  font-size: 1rem;
  font-weight: 500;
  color: #111827;
  margin: 0 0 0.25rem;
}

.item-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.item-price {
  font-size: 1rem;
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
}

.summary-total {
  display: flex;
  justify-content: space-between;
  padding: 1.25rem 0 0.25rem;
  margin-top: 0.75rem;
  border-top: 2px solid #e5e7eb;
}

.total-label {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
}

.total-amount {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
}

/* Terms Section */
.terms-section {
  margin: 0 1.5rem 2rem;
  padding: 1.25rem;
  border-radius: 0.75rem;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
}

.terms-section.terms-error {
  border-color: #ef4444;
  background-color: #fef2f2;
}

.terms-checkbox-container {
  display: flex;
  align-items: flex-start;
}

.checkbox-wrapper {
  position: relative;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  margin-right: 0.75rem;
  margin-top: 0.125rem;
}

.terms-checkbox {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  cursor: pointer;
}

.custom-checkbox {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  border: 2px solid #d1d5db;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.terms-checkbox:checked + .custom-checkbox {
  background-color: #10b981;
  border-color: #10b981;
}

.terms-checkbox:focus + .custom-checkbox {
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.25);
}

.check-icon {
  position: absolute;
  width: 15px;
  height: 15px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.terms-checkbox:checked + .custom-checkbox .check-icon {
  opacity: 1;
}

.terms-label {
  font-size: 0.95rem;
  color: #4b5563;
  cursor: pointer;
  user-select: none;
}

.terms-label a {
  color: #10b981;
  text-decoration: underline;
  font-weight: 500;
}

.terms-label a:hover {
  color: #059669;
}

.terms-error-message {
  margin-top: 0.75rem;
  margin-bottom: 0;
  font-size: 0.875rem;
  color: #ef4444;
  padding-left: 2.25rem;
}

/* Action Button */
.action-container {
  display: flex;
  justify-content: center;
  padding: 0 1.5rem 2rem;
}

.confirm-button {
  display: block;
  width: 100%;
  max-width: 450px;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
  border: none;
  border-radius: 0.75rem;
  padding: 1.25rem 1.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
}

.confirm-button:hover:not(:disabled) {
  box-shadow: 0 8px 16px rgba(16, 185, 129, 0.3);
  transform: translateY(-2px);
}

.confirm-button:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
}

.confirm-button-disabled {
  background: #d1d5db;
  box-shadow: none;
  cursor: not-allowed;
}

.button-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

.button-icon,
.button-icon-disabled {
  width: 20px;
  height: 20px;
  margin-right: 0.75rem;
}

.button-icon-disabled {
  opacity: 0.5;
}

/* Responsive styles */
@media (max-width: 640px) {
  .confirmation-header {
    padding: 1.5rem 1rem;
  }

  .confirmation-title {
    font-size: 1.25rem;
  }

  .confirmation-message,
  .summary-card,
  .terms-section {
    margin-left: 1rem;
    margin-right: 1rem;
  }

  .summary-item {
    flex-direction: column;
  }

  .item-price {
    margin-top: 0.5rem;
  }

  .confirm-button {
    padding: 1rem;
    font-size: 1rem;
  }
}
</style>